import { useAuth0 } from "@auth0/auth0-react";
import { useAtomValue } from "jotai";
import React from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { BillingInfoView } from "~/components/billing-info-view/billing-info-view";
import { AddressView } from "~/components/ui/address-view";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { AdminIcon } from "~/components/ui/icons/admin";
import { AlertIcon } from "~/components/ui/icons/alert";
import { AlertCircleIcon } from "~/components/ui/icons/alert-circle";
import { EuroCircleIcon } from "~/components/ui/icons/euro-circle";
import { InformationIcon } from "~/components/ui/icons/information";
import { RefundIcon } from "~/components/ui/icons/refund";
import { Loader } from "~/components/ui/loader";
import { toast } from "~/components/ui/toast";
import { StoreRegion } from "~/graphql/graphql.gen";
import { useCopyToClipboard } from "~/hooks/use-copy-to-clipboard";
import useTitle from "~/hooks/use-title";
import { track } from "~/libs/analytics";
import type { BrandMatch } from "~/routes/root";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";
import { getFormattedAddress, type Address } from "~/utils/address";
import { getIsInvoiceReady } from "~/utils/billing";
import { formatPrice } from "~/utils/currency";
import { hasBillingErrors, hasMissingBillingInformation } from "~/utils/sales";
import { BillingExportMenu } from "~/views/billing/billing-export-menu";

import { AdminWithTooltip } from "./admin-with-tooltip";
import {
  AnalyticsCard,
  AnalyticsCardContent,
  AnalyticsCardLabel,
  AnalyticsCardValue,
} from "./analytics-card";
import { BillingErrorList } from "./billing-error-list";
import { BillingPayments } from "./billing-payments";
import { applyBillingView, getBillingView } from "./billing-preview";
import { BillingSummary } from "./billing-summary";
import { FinancialAdjustments } from "./financial-adjustments";
import { InvoiceFullyPaidBanner } from "./invoice-fully-paid-banner";
import { InvoiceLocked } from "./invoice-locked";
import { InvoiceUpload } from "./invoice-upload";
import { Invoices } from "./invoices";
import { useBilling } from "./use-billing";
import { useBillingState } from "./use-billing-state";

export function Billing() {
  const { user } = useAuth0();
  const { search } = useLocation();
  const navigate = useNavigate();
  const { brandId = "" } = useParams<BrandMatch>();
  const { i18n, t } = useTranslation();

  const brand = useAtomValue(brandState);
  const activeSale = useAtomValue(activeSaleState);

  const CHOOSE_INFOS: Address & {
    siren: string;
    siret: string;
    tva: string;
  } =
    brand?.store === StoreRegion.Fr
      ? {
          name: "CHOOSE SAS",
          street: "5 rue Pleyel",
          bp: "93200",
          city: "Saint-Denis",
          country: "France",
          countryCode: "FR",
          siren: "812 243 996",
          siret: "81224399600046",
          tva: "FR44812243996",
        }
      : {
          name: "Choose APP LTD.",
          street: "C/O Orbiss Inc\n1411 Broadway, fl16",
          city: "New York",
          bp: "NY 10018",
          country: "United States of America",
          countryCode: "US",
          siren: "812 243 996",
          siret: "81224399600046",
          tva: "FR44812243996",
        };

  const {
    data: billing,
    isLoading: billingLoading,
    error: billingError,
  } = useBilling();
  const { data: billingState } = useBillingState();

  useTitle(`${brand?.name ?? ""} ~ Invoice`);

  const fiscalCountryCode = brand?.billing_address?.countryCode ?? "FR";

  const [_clipboardState, copyToClipboard] = useCopyToClipboard();

  const onBillingAddressClick = async () => {
    const address = await getFormattedAddress(
      {
        company: CHOOSE_INFOS.name,
        address1: CHOOSE_INFOS.street,
        address2: "",
        zip: CHOOSE_INFOS.bp,
        city: CHOOSE_INFOS.city,
        country: "FR",
      },
      i18n.language === "fr" ? "FR" : "EN",
    );
    try {
      copyToClipboard(address.join("\n") || "");
      toast.success(t("clipboard.copied"));
    } catch {
      toast.error(t("clipboard.error"));
    } finally {
      track("CopyToClipboard", {
        label: "billing_address",
      });
    }
  };

  const goToBillingSettings = () => {
    navigate({
      pathname: `/${brandId}/settings/billing`,
      search: search,
    });
  };

  const isInternalUser = applyBillingView(
    Boolean(user?.["https://appchoose.io/claims/isInternalUser"]),
    getBillingView(),
  );

  const isSubjectVat =
    brand?.store === StoreRegion.Fr &&
    fiscalCountryCode === "FR" &&
    !!billing?.is_subject_to_vat;

  const hasMissingBillingInfos = hasMissingBillingInformation(brand);
  const hasBillingError = hasBillingErrors(billingError, billing);
  const locale = i18n.language === "fr" ? "fr" : "en";
  const isInvoiceReady = getIsInvoiceReady(billingState);

  return (
    <main className="relative flex-1 overflow-y-auto focus:outline-none">
      <div className="m-4 flex max-w-4xl flex-col gap-y-10 md:m-10">
        <header>
          <h2 className="text-4xl font-bold leading-[3.75rem] text-gray-900">
            {t("sidebar.billing")}
          </h2>
        </header>
        {isInvoiceReady ? (
          <InvoiceFullyPaidBanner isSubjectVat={isSubjectVat} />
        ) : null}
        <Invoices />
        <BillingPayments isSubjectVat={isSubjectVat} />
        {!isInvoiceReady ? <InvoiceLocked isSubjectVat={isSubjectVat} /> : null}
        {billingLoading ? (
          <Loader className="size-6" />
        ) : (
          <div className="flex flex-col gap-10">
            {!isInvoiceReady && isInternalUser ? (
              <>
                <hr className="h-px w-full border-none bg-gray-100" />
                <Alert>
                  <AdminIcon />

                  <AlertTitle>{t("invoice.tooltip_admin_only")}</AlertTitle>
                  <AlertDescription>
                    {t("invoice.entire_section_not_visible_to_the_brand")}
                  </AlertDescription>
                </Alert>
              </>
            ) : null}
            {hasBillingError ? (
              <Alert appearance="error">
                <InformationIcon />
                <AlertTitle>
                  {t("invoice.invoice_calculation_error_title")}
                </AlertTitle>
                <AlertDescription>
                  {t("invoice.invoice_calculation_error")}
                </AlertDescription>
              </Alert>
            ) : null}
            {isInternalUser ? <BillingErrorList /> : null}
            {isInvoiceReady || isInternalUser ? (
              <div className="flex flex-col gap-6">
                <h3 className="text-2xl font-bold text-gray-900">
                  {t("invoice.invoice_details_and_import")}
                </h3>
                <div className="flex flex-col gap-10">
                  {hasMissingBillingInfos ? (
                    <Alert
                      appearance="error"
                      className="has-[>div[data-slot=alert-action]]:sm:pr-72"
                    >
                      <AlertCircleIcon />
                      <AlertTitle>
                        {t("invoice.missing_billing_infos_title")}
                      </AlertTitle>
                      <AlertDescription>
                        {t("invoice.missing_billing_infos_content")}
                      </AlertDescription>
                      <AlertAction className="hidden sm:block">
                        <Button
                          onClick={goToBillingSettings}
                          type="button"
                          variant="outline"
                          className="border-red-350 hover:border-red-600 focus-visible:ring-2 focus-visible:ring-red-600/30"
                        >
                          {t("invoice.missing_billing_infos_button")}
                        </Button>
                      </AlertAction>
                    </Alert>
                  ) : null}

                  <InvoiceUpload isSubjectVat={isSubjectVat} />

                  <FinancialAdjustments isSubjectVat={isSubjectVat} />

                  {billingState?.canAccessBypassed &&
                  !billingState.canAccess ? (
                    <Alert>
                      <AlertIcon />
                      <AlertTitle>
                        {t("invoice.amount_subject_to_change")}
                      </AlertTitle>
                      <AlertDescription>
                        {t("invoice.orders_are_still_being_processed", {
                          withdrawalPeriod: billingState.withdrawalPeriodInDays,
                        })}
                      </AlertDescription>
                    </Alert>
                  ) : null}

                  <section className="flex flex-col gap-4">
                    <div className="flex justify-between">
                      <h3 className="text-xl font-bold">
                        {t("invoice.title")}
                      </h3>
                      <BillingExportMenu />
                    </div>

                    {isInternalUser ? (
                      <div className="flex w-full flex-row gap-4">
                        <AnalyticsCard className="relative w-full">
                          <AdminWithTooltip className="absolute right-4 top-4" />
                          <AnalyticsCardContent>
                            <div className="flex space-x-2 text-gray-700">
                              <EuroCircleIcon className="size-4" />
                              <AnalyticsCardLabel>
                                {t("invoice.total_operation_ttc")}
                              </AnalyticsCardLabel>
                            </div>
                            <AnalyticsCardValue>
                              {formatPrice(
                                (billing?.total_operation_ttc ?? 0) / 100,
                                locale,
                                activeSale?.currency.toString().toUpperCase() ??
                                  "EUR",
                              )}
                            </AnalyticsCardValue>
                          </AnalyticsCardContent>
                        </AnalyticsCard>

                        <AnalyticsCard className="relative w-full">
                          <AdminWithTooltip className="absolute right-4 top-4" />
                          <AnalyticsCardContent>
                            <div className="flex space-x-2 text-gray-700">
                              <RefundIcon />
                              <AnalyticsCardLabel>
                                {t("invoice.total_refund_ttc")}
                              </AnalyticsCardLabel>
                            </div>
                            <AnalyticsCardValue>
                              {formatPrice(
                                (billing?.total_refund_ttc ?? 0) / 100,
                                locale,
                                activeSale?.currency.toString().toUpperCase() ??
                                  "EUR",
                              )}
                            </AnalyticsCardValue>
                          </AnalyticsCardContent>
                        </AnalyticsCard>
                      </div>
                    ) : null}
                    <BillingSummary
                      isInternalUser={isInternalUser}
                      isSubjectVat={isSubjectVat}
                    />
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-xl font-bold">
                      {t("invoice.choose_billing_infos_title")}
                    </h3>
                    <div className="justify-between space-x-0 space-y-4 md:flex md:space-x-4 md:space-y-0">
                      <div className="w-full rounded-lg border border-gray-100 p-6">
                        <AddressView
                          label={t("address.label")}
                          address={CHOOSE_INFOS}
                          locale={i18n.language === "fr" ? "FR" : "EN"}
                        />
                        <div className="mt-4 flex space-x-12">
                          {brand?.store === StoreRegion.Fr ? (
                            <>
                              <div>
                                <dt className="pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                  {t("billing.fields.siret.label")}
                                </dt>
                                <dd className="text-sm text-gray-700">
                                  {CHOOSE_INFOS.siret}
                                </dd>
                              </div>

                              <div>
                                <dt className="pb-1.5 text-xs font-semibold uppercase tracking-wider text-gray-500">
                                  {t("billing.fields.intracommunity_vat.label")}
                                </dt>
                                <dd className="text-sm text-gray-700">
                                  {CHOOSE_INFOS.tva}
                                </dd>
                              </div>
                            </>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          className="mt-6 text-xs font-bold text-green-900 focus-visible:ring-2 focus-visible:ring-gray-900/30"
                          onClick={onBillingAddressClick}
                        >
                          {t("copy")}
                        </button>
                      </div>
                    </div>
                  </section>
                  <section className="flex flex-col gap-4">
                    <h3 className="text-xl font-bold">
                      {t("invoice.brand_billing_infos_title")}
                    </h3>
                    <div className="min-w-[18.375rem] rounded-lg border border-gray-100 p-6">
                      <BillingInfoView
                        isSiretActive={
                          fiscalCountryCode === "FR" &&
                          brand?.store === StoreRegion.Fr
                        }
                        address={brand?.billing_address ?? undefined}
                        iban={brand?.iban ?? undefined}
                        intracommunityVat={
                          brand?.intracommunity_vat ?? undefined
                        }
                        isSubjectVat={brand && !brand.is_autoentrepreneur}
                        siret={brand?.siret ?? undefined}
                        taxId={brand?.tax_id ?? undefined}
                        onEditBankInfo={goToBillingSettings}
                        onEditBillingInfo={goToBillingSettings}
                        onEditIban={goToBillingSettings}
                      />
                    </div>
                  </section>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </main>
  );
}
